import React, { useCallback, useEffect, useState } from 'react'
import { Form, Message } from 'semantic-ui-react'
import { AppPrimaryButton } from 'elements'
import { LoaderBar } from 'components'
import { AutoTicketService } from 'utility/services/auto-ticket'
import { useNotification } from 'hooks/toast'
import {
    getApiErrorMessage,
    normalizeCompanyTicketSetting,
} from './utils'

export const CompanyTicketSetting = () => {
    const { successToast, errorToast } = useNotification()
    const [releaseOn, setReleaseOn] = useState(2)
    const [notificationMode, setNotificationMode] = useState(1)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [forbidden, setForbidden] = useState(false)
    const [error, setError] = useState('')

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        setForbidden(false)
        try {
            const res = await AutoTicketService.getCompanySetting()
            const setting = normalizeCompanyTicketSetting(res)
            setReleaseOn(setting.releaseOn === 1 ? 1 : 2)
            setNotificationMode(setting.notificationMode === 2 ? 2 : 1)
        } catch (err) {
            if (err?.status === 403) {
                setForbidden(true)
            } else {
                setError(getApiErrorMessage(err))
            }
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const handleSave = async () => {
        setSaving(true)
        setError('')
        try {
            await AutoTicketService.saveCompanySetting({
                notificationMode,
                releaseOn,
            })
            successToast('Corrective-action ticket settings saved')
        } catch (err) {
            if (err?.status === 403) {
                setForbidden(true)
                errorToast('Only an admin can change these settings.')
            } else {
                const message = getApiErrorMessage(err)
                setError(message)
                errorToast(message)
            }
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return <LoaderBar content="Loading ticket settings…" />
    }

    if (forbidden) {
        return (
            <Message warning>
                Only an admin can view or change corrective-action ticket
                settings.
            </Message>
        )
    }

    return (
        <Form>
            <h3 style={{ marginTop: 0 }}>Corrective-action tickets</h3>
            {error && (
                <Message negative onDismiss={() => setError('')}>
                    {error}
                </Message>
            )}
            <Form.Field>
                <label>When should tickets be sent?</label>
                <Form.Radio
                    label="When the inspection is completed"
                    name="releaseOn"
                    checked={releaseOn === 1}
                    onChange={() => setReleaseOn(1)}
                />
                <Form.Radio
                    label="When the report is approved"
                    name="releaseOn"
                    checked={releaseOn === 2}
                    onChange={() => setReleaseOn(2)}
                />
            </Form.Field>
            <Form.Field>
                <label>How should owners be emailed?</label>
                <Form.Radio
                    label="One summary email per person per inspection"
                    name="notificationMode"
                    checked={notificationMode === 1}
                    onChange={() => setNotificationMode(1)}
                />
                <Form.Radio
                    label="One email per ticket"
                    name="notificationMode"
                    checked={notificationMode === 2}
                    onChange={() => setNotificationMode(2)}
                />
            </Form.Field>
            <AppPrimaryButton
                type="button"
                onClick={handleSave}
                loading={saving}
                disabled={saving}
            >
                Save
            </AppPrimaryButton>
        </Form>
    )
}
